import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import wuloojMark from "assets/images/superadmin/wulooj-mark.png";
import { usePortalTenant } from "context/PortalTenantContext";
import {
  applyTenantBranding,
  clearTenantBranding,
  getTenantBrandingFromStorage,
  normalizeTenantBranding,
  saveTenantBrandingToStorage,
} from "utils/tenantBranding";

const resolveActiveTenantBranding = ({
  isSuperAdmin,
  authTenantBranding,
  portalTenantBranding,
}) => {
  if (isSuperAdmin) {
    return null;
  }

  const storedBranding = getTenantBrandingFromStorage();
  const source =
    storedBranding || authTenantBranding || portalTenantBranding || null;

  return source ? normalizeTenantBranding(source) : null;
};

const TenantThemeBridge = () => {
  const accountType = useSelector((state) => state.Login?.accountType);
  const accountScope = useSelector((state) => state.Login?.accountScope);
  const authTenantBranding = useSelector(
    (state) => state.Login?.authUser?.tenantBranding || null
  );
  const { tenantBranding: portalTenantBranding } = usePortalTenant();

  const isSuperAdmin =
    accountScope === "super_admin" || accountType === "superadmin";

  const activeBranding = useMemo(
    () =>
      resolveActiveTenantBranding({
        isSuperAdmin,
        authTenantBranding,
        portalTenantBranding,
      }),
    [isSuperAdmin, authTenantBranding, portalTenantBranding]
  );

  useEffect(() => {
    if (isSuperAdmin) {
      clearTenantBranding();
      return;
    }

    if (activeBranding) {
      if (!getTenantBrandingFromStorage() && authTenantBranding) {
        saveTenantBrandingToStorage(activeBranding);
      }
      applyTenantBranding(activeBranding);
    }
  }, [isSuperAdmin, activeBranding, authTenantBranding]);

  useEffect(() => {
    const link =
      document.querySelector("link[rel='icon']") ||
      Object.assign(document.createElement("link"), { rel: "icon" });
    if (!link.parentNode) document.head.appendChild(link);

    const prevHref = link.getAttribute("href") || "";
    const logoUrl = activeBranding?.logoUrl?.trim() || "";
    link.href = isSuperAdmin ? wuloojMark : logoUrl || wuloojMark;

    const metaTheme = document.querySelector("meta[name='theme-color']");
    const prevTheme = metaTheme?.getAttribute("content");
    if (!isSuperAdmin && metaTheme && activeBranding) {
      metaTheme.setAttribute(
        "content",
        activeBranding.buttons?.primaryBackgroundColor ||
          activeBranding.sidebar?.backgroundColor ||
          "#556EE6"
      );
    }

    return () => {
      if (prevHref) link.setAttribute("href", prevHref);
      if (metaTheme && prevTheme !== undefined) {
        metaTheme.setAttribute("content", prevTheme || "#000000");
      }
    };
  }, [isSuperAdmin, activeBranding]);

  return null;
};

export default TenantThemeBridge;
